import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type { SubmitPlatformFeedbackRequest, SubmitPlatformFeedbackResponse } from '../models/platform-feedback.models';

@Injectable({ providedIn: 'root' })
export class PlatformFeedbackService {
  private readonly http = inject(HttpClient);

  /**
   * Upload de imagem para anexar ao feedback (multipart, campo `file`).
   * Endpoint alinhado ao padrão de anexos do produto; ajustar se o backend usar outro path.
   */
  async uploadImageAttachment(file: File): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await firstValueFrom(
      this.http.post<unknown>('/api/platform-feedback/attachments', formData)
    );
    const id = this.extractAttachmentId(response);
    if (!id) {
      throw new Error('missing-attachment-id');
    }
    return id;
  }

  async submit(payload: SubmitPlatformFeedbackRequest): Promise<SubmitPlatformFeedbackResponse> {
    const response = await firstValueFrom(
      this.http.post<unknown>('/api/platform-feedback/submit', payload)
    );
    const rec =
      response && typeof response === 'object' && !Array.isArray(response)
        ? (response as Record<string, unknown>)
        : {};
    return {
      id: typeof rec['id'] === 'string' ? rec['id'] : undefined,
      success: typeof rec['success'] === 'boolean' ? rec['success'] : undefined,
    };
  }

  private extractAttachmentId(raw: unknown): string | null {
    const rec = this.asRecord(raw);
    const data = this.asRecord(rec['data']);
    const attachment = this.asRecord(rec['attachment']);
    const nested = this.asRecord(rec['result'] ?? rec['payload']);
    const candidates: unknown[] = [
      rec['id'],
      rec['attachmentId'],
      rec['AttachmentId'],
      data['id'],
      data['attachmentId'],
      attachment['id'],
      nested['id'],
    ];
    for (const c of candidates) {
      if (typeof c === 'string' && c.trim()) {
        return c.trim();
      }
    }
    return null;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  }
}

export function isPlatformFeedbackConfigurationError(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) return false;
  const body = error.error;
  if (!body || typeof body !== 'object') return false;
  const msg = String((body as Record<string, unknown>)['message'] ?? '').toLowerCase();
  const key = String((body as Record<string, unknown>)['key'] ?? '').toLowerCase();
  return msg.includes('config') || key.includes('config');
}
